import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface MetaspaceEvent {
  id: string;
  type:
    | 'CHAT_TRIGGERED'
    | 'TOOLBAR_EXPAND'
    | 'THREAD_CONTEXT_LOAD'
    | 'SPHERE_CREATED'
    | 'SPHERE_UPDATED'
    | 'SPHERE_STATUS_CHANGED'
    | 'OPEN_SPHERE_EDITOR'
    | 'TASK_FILTER_TRIGGERED'
    | 'SPHERE_CENTERED'
    | 'TIMELINE_TRIGGERED'
    | 'CONVERSATION_MESSAGE_CLICKED';
  payload: {
    sphereId?: string;
    sphereType?: string;
    metaspaceId?: string;
    threadId?: string;
    messageId?: string;
    contextKey?: string;
    parentId?: string;
    ancestorIds?: string[];
    initialTab?: string;
    timestamp: number;
    [key: string]: any;
  };
}

interface MetaspaceEventsState {
  events: MetaspaceEvent[];
  lastEvent: MetaspaceEvent | null;
  subscribers: {
    [eventType: string]: string[];
  };
}

const initialState: MetaspaceEventsState = {
  events: [],
  lastEvent: null,
  subscribers: {},
};

const metaspaceEventsSlice = createSlice({
  name: 'metaspaceEvents',
  initialState,
  reducers: {
    emitEvent: (state, action: PayloadAction<Omit<MetaspaceEvent, 'id'>>) => {
      // Use crypto.randomUUID() for secure, unpredictable IDs
      // Falls back to secure timestamp-based ID if crypto.randomUUID() not available
      let secureId: string;
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        secureId = `event_${crypto.randomUUID()}`;
      } else {
        // Fallback: use crypto.getRandomValues() for secure random bytes
        const randomBytes = new Uint8Array(16);
        if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
          crypto.getRandomValues(randomBytes);
          secureId = `event_${Date.now()}_${Array.from(randomBytes, byte => byte.toString(36)).join('').slice(0, 16)}`;
        } else {
          // Final fallback for very old browsers - still better than Math.random()
          secureId = `event_${Date.now()}_${performance.now().toString(36).replace('.', '')}_${Date.now().toString(36)}`;
        }
      }

      const event: MetaspaceEvent = {
        id: secureId,
        ...action.payload,
      };

      state.events.push(event);
      state.lastEvent = event;

      // Keep only the last 50 events to prevent memory leaks
      if (state.events.length > 50) {
        state.events = state.events.slice(-50);
      }
    },

    clearEvents: (state) => {
      state.events = [];
      state.lastEvent = null;
    },

    subscribeToEvent: (
      state,
      action: PayloadAction<{ eventType: string; subscriberId: string }>
    ) => {
      const { eventType, subscriberId } = action.payload;
      if (!state.subscribers[eventType]) {
        state.subscribers[eventType] = [];
      }
      if (!state.subscribers[eventType].includes(subscriberId)) {
        state.subscribers[eventType].push(subscriberId);
      }
    },

    unsubscribeFromEvent: (
      state,
      action: PayloadAction<{ eventType: string; subscriberId: string }>
    ) => {
      const { eventType, subscriberId } = action.payload;
      if (state.subscribers[eventType]) {
        state.subscribers[eventType] = state.subscribers[eventType].filter(
          id => id !== subscriberId
        );
      }
    },
  },
});

export const {
  emitEvent,
  clearEvents,
  subscribeToEvent,
  unsubscribeFromEvent,
} = metaspaceEventsSlice.actions;

export default metaspaceEventsSlice.reducer;